// ============================================================
// HIDDEN FIGURES - people only the GM can see (plan map-display, G1).
//
// A hidden figure is never on the shared roster: it lives in the GM-only
// `gmHidden` collection, one document per figure (ADR 2026-10-07). On the
// GM's screen it is part of the WORKING roster, so selecting it and
// attacking with it need nothing new; the sync layer is handed only the
// shared slice (`splitHidden`). When it acts and the GM keeps it hidden, the
// act goes out VEILED: every string a player could read has its name and id
// replaced by its alias ("Unknown N", numbered, never reused).
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from './types';

/** What the turn order needs to put a revealed figure back where it was. The one definition (hooks, scenes). */
export interface StoredInitiative {
  value: number | null;
  roll: number | null;
  bonus: number | null;
}

/** A position, carried while hidden: the battlefield's token, as it was. */
type HiddenToken = Record<string, unknown> & { id: string };

/** One hidden figure: what `gmHidden/<id>` holds. */
export interface HiddenFigure {
  id: string;
  combatant: Combatant;
  token: HiddenToken | null;
  alias: string;
  initiative: StoredInitiative | null;
  /** Never public: its name is a secret too, not only its id. */
  bornHidden: boolean;
}

/** What a hidden figure keeps across a scene's park: its alias, its kept initiative, whether it was ever public. */
export type HiddenMark = Pick<HiddenFigure, 'alias' | 'initiative' | 'bornHidden'>;

const ALIAS = /^Unknown (\d+)$/;

/** The next alias: one above the highest taken, so a revealed figure frees no number. */
export function aliasFor(taken: readonly string[]): string {
  const highest = taken.reduce((n, a) => Math.max(n, Number(ALIAS.exec(a)?.[1] ?? 0)), 0);
  return `Unknown ${highest + 1}`;
}

interface HideOptions {
  token: HiddenToken | null;
  initiative: StoredInitiative | null;
  /** The aliases already used in this session. */
  taken: readonly string[];
  bornHidden: boolean;
}

/** The record a figure is kept in while hidden. */
export function hideFigure(combatant: Combatant, o: HideOptions): HiddenFigure {
  return {
    id: combatant.id,
    combatant,
    token: o.token,
    alias: aliasFor(o.taken),
    initiative: o.initiative,
    bornHidden: o.bornHidden,
  };
}

/** Never written to: the empty hidden part, one instance. */
const NOTHING: never[] = [];

/**
 * The working roster split into what the sync layer may see and what it may
 * not, in order. With nothing hidden the shared slice IS the roster, so a
 * player's screen (and a GM's with no secrets) sees no change of identity.
 */
export function splitHidden<T extends { id: string }>(roster: T[], hiddenIds: ReadonlySet<string>) {
  // The same empty list every time: an effect keyed on it does not run again for nothing.
  if (!hiddenIds.size || !roster.some((c) => hiddenIds.has(c.id))) return { shared: roster, hidden: NOTHING as T[] };
  return { shared: roster.filter((c) => !hiddenIds.has(c.id)), hidden: roster.filter((c) => hiddenIds.has(c.id)) };
}

/** The shared slice with the hidden figures put back after it; a shared copy of a hidden id is dropped. */
export function withHidden<T extends { id: string }>(shared: T[], hidden: T[], hiddenIds: ReadonlySet<string>): T[] {
  if (!hidden.length) return shared;
  return [...shared.filter((c) => !hiddenIds.has(c.id)), ...hidden];
}

/** One secret to keep out of what players read: an id or a name, and what to say instead. */
export interface VeilKey {
  secret: string;
  alias: string;
}

/**
 * What to veil, longest first (so a long secret is replaced before a short
 * one inside it), with the public names that contain a secret kept aside so
 * they are never rewritten ("Gunnery Sergeant Vael" stays whole when "Gunner"
 * is hidden).
 */
export function veilKeys(figures: readonly { id: string; alias: string }[], roster: readonly Combatant[]): VeilKey[] {
  const names = new Map(roster.map((c) => [c.id, c.name]));
  const keys = figures.flatMap((f) => {
    const name = names.get(f.id);
    return [{ secret: f.id, alias: f.alias }, ...(name ? [{ secret: name, alias: f.alias }] : [])];
  });
  return keys.sort((a, b) => b.secret.length - a.secret.length);
}

/** A string as a literal inside a RegExp. */
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Any of these, as whole words: never inside a longer word ("Ork" is not in
 * "Orkish") nor at the start of a longer id. A "-" is NOT part of a word, so
 * an id joined into a composite id (`<tabId>-<ts>-<n>`) is still found. The
 * leak oracle (engine/fuzz/hiddenLeak) looks with the same rule.
 */
export const wholeWords = (words: readonly string[], flags = 'u') =>
  new RegExp(`(?<![\\p{L}\\p{N}_])(?:${words.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}_])`, flags);

/**
 * The veil as one function, compiled ONCE: a single alternation over the
 * secrets (longest first, so a long secret wins over a short one inside it),
 * and the public names that contain a secret kept aside so they are never
 * rewritten ("Gunnery Sergeant Vael" stays whole when "Gunner" is hidden).
 * A string with no secret comes back as it was, and so does any object or
 * array none of whose parts changed. Object KEYS are veiled too: a record
 * keyed by a hidden id would carry it otherwise.
 */
function compileVeil(keys: readonly VeilKey[], publicNames: readonly string[]): (value: unknown) => unknown {
  const alias = new Map(keys.map((k) => [k.secret, k.alias]));
  const find = wholeWords(
    keys.map((k) => k.secret),
    'gu',
  );
  const guarded = publicNames.filter((n) => keys.some((k) => n !== k.secret && n.includes(k.secret)));
  const text = veilText(find, alias, guarded);
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') return text(value);
    if (Array.isArray(value)) return sameOr(value, value.map(walk));
    if (value && typeof value === 'object') return veilEntries(value as Record<string, unknown>, text, walk);
    return value;
  };
  return walk;
}

/** One string veiled: back as it was with no secret in it; the guarded public names kept whole. */
function veilText(find: RegExp, alias: Map<string, string>, guarded: readonly string[]) {
  return (t: string): string => {
    find.lastIndex = 0;
    if (!find.test(t)) return t;
    // Guarded public names are swapped out for placeholders, the secrets replaced, and the names put back.
    const out = guarded.reduce((x, n, i) => x.split(n).join(`\u0000${i}\u0000`), t);
    const veiled = out.replace(find, (m) => alias.get(m) ?? m);
    return guarded.reduce((x, n, i) => x.split(`\u0000${i}\u0000`).join(n), veiled);
  };
}

/** The new array, or the old one when no element changed. */
const sameOr = <T>(old: T[], next: T[]) => (next.every((v, i) => v === old[i]) ? old : next);

/** An object with its keys and values veiled, or the same object when nothing in it changed. */
function veilEntries(
  value: Record<string, unknown>,
  text: (t: string) => string,
  walk: (v: unknown) => unknown,
): Record<string, unknown> {
  const entries = Object.entries(value);
  const next = entries.map(([k, v]) => [text(k), walk(v)] as const);
  const changed = next.some(([k, v], i) => k !== entries[i][0] || v !== entries[i][1]);
  return changed ? Object.fromEntries(next) : value;
}

/**
 * A value as a player may read it: every string in it, however deep, with the
 * hidden figures' ids and names replaced by their aliases. Numbers, booleans
 * and nulls are left alone; with no keys the value comes back as it was.
 */
export function veil<T>(value: T, keys: readonly VeilKey[], publicNames: readonly string[] = []): T {
  if (!keys.length) return value;
  return compileVeil(keys, publicNames)(value) as T;
}

/**
 * What the sync layer applies at its two boundaries (the shared log, the
 * defence document): the keys, the public names to guard, and the alias of
 * the figure the GM is acting as, if it is hidden.
 */
export interface Veil {
  apply: <T>(value: T) => T;
  hides: (id: string | null | undefined) => boolean;
  actingAlias: string | null;
}

/** Nobody hidden: a veil that changes nothing. */
export const NO_VEIL: Veil = { apply: (value) => value, hides: () => false, actingAlias: null };

/** The veil for this table: the GM's hidden figures, the working roster's names, the GM's current actor. */
export function makeVeil(
  figures: readonly { id: string; alias: string }[],
  roster: readonly Combatant[],
  actorId: string | null | undefined,
): Veil {
  if (!figures.length) return NO_VEIL;
  const hidden = new Map(figures.map((f) => [f.id, f.alias]));
  const publicNames = roster.filter((c) => !hidden.has(c.id)).map((c) => c.name);
  const veiled = compileVeil(veilKeys(figures, roster), publicNames);
  return {
    apply: <T>(value: T) => veiled(value) as T,
    hides: (id) => !!id && hidden.has(id),
    actingAlias: (actorId && hidden.get(actorId)) || null,
  };
}

/** A defence document as the victim's player may read it: veiled, and naming the alias when a hidden figure fired. */
export function veilDefence<T extends Record<string, unknown>>(v: Veil, doc: T): T {
  const veiled = v.apply(doc);
  return v.actingAlias ? { ...veiled, attackerName: v.actingAlias } : veiled;
}

/** The document `gmHidden/<id>` holds: the figure as text, so no nested `undefined` can refuse the write (gotcha 2). */
export function figureToDoc(fig: HiddenFigure, now: number): { data: string; updatedAt: number } {
  return { data: JSON.stringify(fig), updatedAt: now };
}

/** The record's JSON, or null when it is not a record at all. */
function parsed(doc: { data?: unknown } | null | undefined): Record<string, unknown> | null {
  if (!doc || typeof doc.data !== 'string') return null;
  try {
    const value = JSON.parse(doc.data);
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
}

/** A parsed record as a figure, or null for anything damaged or ended: never half a figure. */
function figureFrom(fig: Record<string, unknown> | null): HiddenFigure | null {
  const whole = fig && typeof fig.id === 'string' && fig.combatant && typeof fig.alias === 'string' && !fig.endedAt;
  return whole ? (fig as unknown as HiddenFigure) : null;
}

/** A figure read back from its document (see `figureFrom`). */
export function figureFromDoc(doc: { data?: unknown } | null | undefined): HiddenFigure | null {
  return figureFrom(parsed(doc));
}

/**
 * A figure no longer hidden (revealed, or removed by the GM) is not deleted:
 * its document becomes a TOMBSTONE that keeps only its alias, so the next
 * figure hidden is never given the same "Unknown N" (ruling 2026-10-07).
 */
export function endedToDoc(fig: { id: string; alias: string }, now: number): { data: string; updatedAt: number } {
  return { data: JSON.stringify({ id: fig.id, alias: fig.alias, endedAt: now }), updatedAt: now };
}

/** What one stored record says: the live figure (or null), the alias it holds, and whether it has ended. */
export function readHiddenDoc(doc: { data?: unknown } | null | undefined) {
  const value = parsed(doc);
  return {
    figure: figureFrom(value),
    alias: typeof value?.alias === 'string' ? value.alias : null,
    ended: !!value?.endedAt,
  };
}
